import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Button, Stack, IconButton, useTheme, useMediaQuery } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import BookingModal from './BookingModal';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

function HeroSlider({ width = 1200, height = 420, children }) {
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [open, setOpen] = useState(false);
  const [isSliding, setIsSliding] = useState(false);
  const navigate = useNavigate();
  const autoSlideRef = useRef();
  const touchStartX = useRef(null);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    api.get('/api/cms/sliders')
      .then(res => setSlides(res.data.map(s => s.contentJSON)))
      .catch(() => setSlides([]));
  }, []);

  // Auto-slide every 5 seconds
  useEffect(() => {
    autoSlideRef.current = setInterval(() => {
      setCurrent(c => slides.length ? (c + 1) % slides.length : 0);
      setIsSliding(true);
      setTimeout(() => setIsSliding(false), 600);
    }, 5000);
    return () => clearInterval(autoSlideRef.current);
  }, [slides.length]);

  // Touch/swipe support
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      if (delta > 0) prevSlide();
      else nextSlide();
    }
    touchStartX.current = null;
  };

  const nextSlide = () => {
    setIsSliding(true);
    setCurrent(c => (slides.length ? (c + 1) % slides.length : 0));
    setTimeout(() => setIsSliding(false), 600);
  };
  const prevSlide = () => {
    setIsSliding(true);
    setCurrent(c => (slides.length ? (c - 1 + slides.length) % slides.length : 0));
    setTimeout(() => setIsSliding(false), 600);
  };
  const slide = slides[current] || { title: '', desc: '', image: '', style: '' };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: '100%', md: '100%', lg: 1600 },
          minWidth: { xs: '100%', sm: '100%', md: 320, lg: 600 },
          mx: { xs: 0, md: 'auto' },
          px: { xs: 0, md: 0, lg: 2 }, // minimal horizontal padding for desktop
          // Respect passed `height` on all breakpoints so images can be large on mobile
          minHeight: height,
          height: height,
          maxHeight: { xs: 'none', md: height },
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.900',
          borderRadius: { xs: 3, md: 2, lg: 1 }, // less rounded on desktop
          boxShadow: 4,
          mb: { xs: 2, md: 4 },
          overflow: 'hidden',
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Slide image with fade transition */}
        {/* Use background-image cover on larger screens, but render an <img> with object-fit:contain on XS to avoid mobile zoom/cropping. */}
        <Box sx={{ display: { xs: 'none', sm: 'block' }, position: 'absolute', inset: 0, zIndex: 0, backgroundImage: slide.image ? `url(${slide.image})` : 'none', backgroundSize: 'cover', backgroundPosition: isXs ? 'center top' : 'center center', backgroundRepeat: 'no-repeat', opacity: isSliding ? 0.7 : 1, transition: 'opacity 0.6s, background-image 0.5s', transform: 'translateZ(0)', }} />
        {slide.image && (
          <Box component="img" src={slide.image} alt={slide.title || slide.desc} sx={{ display: { xs: 'block', sm: 'none' }, position: 'absolute', inset: 0, zIndex: 0, width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center top', backgroundColor: 'grey.900', opacity: isSliding ? 0.95 : 1, transition: 'opacity 0.45s' }} />
        )}
        {/* Overlay for dark effect (lighter on mobile so images are more visible) */}
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: isXs ? 'rgba(0,0,0,0.24)' : 'rgba(0,0,0,0.45)', zIndex: 1, pointerEvents: 'none' }} />
        {/* Carousel navigation */}
        {slides.length > 1 && (
          <>
            <IconButton onClick={prevSlide} sx={{ position: 'absolute', left: 16, top: '50%', zIndex: 3, color: '#fff', bgcolor: 'rgba(0,0,0,0.32)', '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' } }}>
              <ArrowBackIosNewIcon />
            </IconButton>
            <IconButton onClick={nextSlide} sx={{ position: 'absolute', right: 16, top: '50%', zIndex: 3, color: '#fff', bgcolor: 'rgba(0,0,0,0.32)', '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' } }}>
              <ArrowForwardIosIcon />
            </IconButton>
          </>
        )}
        <Box sx={{
          position: 'relative',
          zIndex: 2,
          textAlign: { xs: 'center', md: 'left' },
          width: { xs: '100%', md: '60%' },
          px: { xs: 3, md: 0 },
          pl: { md: 6 },
          py: { xs: 1.5, md: 0 },
          pt: { md: 10 },
          alignItems: { xs: 'center', md: 'flex-start' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: { xs: 180, md: height - 60 },
          transform: { xs: 'translateY(-2%)', md: 'none' },
          gap: 1
        }}>
          <Typography variant="h4" fontWeight="bold" color="primary.contrastText" sx={{ mb: 1.5, fontSize: { xs: '1.4rem', sm: '1.9rem', md: '3.5rem' }, textShadow: '0 2px 12px rgba(0,0,0,0.3)', lineHeight: 1.02, whiteSpace: 'normal' }}>
            {slide.title}
          </Typography>
          <Typography variant="body1" color="primary.contrastText" sx={{ fontWeight: 400, mb: 2, fontSize: { xs: '0.98rem', sm: '1.15rem', md: '1.5rem' }, textShadow: '0 1px 8px rgba(0,0,0,0.2)', lineHeight: 1.35, maxWidth: { xs: '100%', md: '90%' } }}>
            {slide.desc}
          </Typography>
          {/* CTAs are provided by the page overlay (HomePage), so omit duplicated buttons here */}
        </Box>
        {/* Dots indicator */}
        {slides.length > 1 && (
          <Box sx={{ position: 'absolute', bottom: 24, left: 0, width: '100%', display: 'flex', justifyContent: 'center', zIndex: 3 }}>
            {slides.map((_, idx) => (
              <Box key={idx} sx={{ width: 12, height: 12, borderRadius: '50%', mx: 0.5, bgcolor: idx === current ? 'primary.main' : 'grey.500', transition: 'all 0.2s' }} />
            ))}
          </Box>
        )}
        {/* Render any page-provided overlay CTAs or children above slider content */}
        {children && (
          <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 20, pointerEvents: 'none' }}>
            {/* children may handle their own pointerEvents; ensure they receive events */}
            <Box sx={{ pointerEvents: 'auto' }}>
              {children}
            </Box>
          </Box>
        )}
      </Box>
      <BookingModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export default HeroSlider;
